/**
 * C-07 «Чат» и C-21 «Закрытый диалог».
 *
 * ПС §11: шапка с собеседником, история, поле ввода, вложения, голосовая
 * запись, статус соединения при проблемах. После block/unmatch поле отправки
 * отключается, а история остаётся доступной для чтения (решение D-06).
 */
import { useNavigate, useParams, useSearch, useRouter, useRouterState } from '@tanstack/react-router'
import { lazy, useCallback, useEffect, useLayoutEffect, useRef, useState, Suspense } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { faceOf, spaceIcon, subscribersLabel } from '@entities/conversation/display'
import { useConversation, useMessages } from '@entities/conversation/model'
import { useChannelMembers } from '@features/channels/use-channel-members'
import { useChatActions } from '@features/chat/use-chat-actions'
import { deleteBatch } from '@features/chat/delete-batch'
import { enqueueForwardBatch } from '@features/chat/forward-batch'
import { useReadReceipts } from '@features/chat/use-read-receipts'
import { MESSAGE_REQUESTS_KEY } from '@features/chat/use-message-requests'
import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { ChannelTopic, Message } from '@shared/api/types'
import type { LocalMediaDraft, LocalMessage } from '@shared/db'
import { UNCONFIRMED_STATES, conversationsRepo, messagesRepo, uiStateRepo, UiKey } from '@shared/db'
import { ReportDialog, type ReportTarget } from '@features/report/ReportDialog'
import type { ParsedLink } from '@shared/lib/links'
import { useOverlayHistory } from '@shared/lib/overlay-history'
import { canDeleteForAll, canEditMessage } from '@shared/lib/message-rules'
import {
  ActionItem,
  ActionList,
  Avatar,
  Button,
  Dialog,
  EmptyState,
  Icon,
  Sheet,
  Spinner,
  cx,
  useToast,
} from '@shared/ui'

import { hotkeysAvailable } from '../../model/hotkeys'
import { hasInAppHistory } from '../../model/navigation-direction'
import { ScreenFrame, type ScreenFrameHandle } from '../../layouts/ScreenFrame'
import { rememberHistoryAnchor, takeHistoryAnchor } from '../../model/scroll-memory'
import { setActiveConversation } from '../../boot/active-chat'
import { selectIsReadOnly, useAppStore } from '../../model/app-store'
import { offerPushPrompt } from '../../model/push-prompt'
import { useTyping } from '../../model/typing-store'
import { formatPresence } from '../../lib/format'
import { Composer, type ComposerMode } from './Composer'
import { ChatSearch } from './ChatSearch'
import { ForwardSheet } from './ForwardSheet'
import { ExternalLinkDialog } from './ExternalLinkDialog'
import { MessageHistory, type MessageHistoryHandle } from './MessageHistory'
import { mediaLabel } from './message-text'
import { MUTE_OPTIONS } from './mute-options'
import styles from './message.module.css'

/*
 * Полоса канала нужна только на ленте канала — отдельный чанк, чтобы не занимать бюджет
 * маршрута переписки (§11.1).
 */
const ChannelFooter = lazy(async () => ({
  default: (await import('../channels/ChannelFooter')).ChannelFooter,
}))

/*
 * Просмотрщик вложений открывают нечасто, а весит он много (жесты, зум, свайпы) — отдельный
 * чанк, чтобы не занимать бюджет маршрута переписки (§11.1).
 */
const MediaViewer = lazy(async () => ({
  default: (await import('./MediaViewer')).MediaViewer,
}))

/**
 * SF-02: к жалобе на сообщение прикладывается контекст переписки — соседние
 * сообщения вокруг спорного, чтобы решение принималось по разговору.
 */
const REPORT_CONTEXT_RADIUS = 5

function messageReportTarget(
  message: LocalMessage,
  history: readonly LocalMessage[],
  counterpartName: string | null,
): ReportTarget {
  const index = history.findIndex((item) => item.id === message.id)
  const from = Math.max(0, index - REPORT_CONTEXT_RADIUS)
  const context = index < 0 ? [] : history.slice(from, index + REPORT_CONTEXT_RADIUS + 1)

  return {
    type: 'message',
    id: message.id,
    title: 'Пожаловаться на сообщение',
    subject: counterpartName,
    // Неподтверждённые записи серверу неизвестны — их идентификаторы локальные.
    contextMessageIds: context.filter((item) => !item.id.startsWith('local:')).map((item) => item.id),
  }
}

interface ChatScreenProps {
  /** В двухпанельном режиме id приходит снаружи, а не из маршрута. */
  conversationIdOverride?: string
  embedded?: boolean
}

export function ChatScreen({ conversationIdOverride, embedded = false }: ChatScreenProps = {}) {
  const params = useParams({ strict: false }) as { conversationId?: string; topicId?: string }
  const conversationId = conversationIdOverride ?? params.conversationId ?? null
  const topicId = params.topicId ?? null

  const navigate = useNavigate()
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const actions = useChatActions()
  const frameRef = useRef<ScreenFrameHandle>(null)

  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  // BS §6.3: санкция `read_only` / `banned` запрещает отправку (SYS-16).
  const readOnly = useAppStore(selectIsReadOnly)
  const myUserId = me?.user.id ?? ''

  const conversation = useConversation(conversationId)
  const pinned = conversation?.pinned_message ?? null
  const { items: allMessages, loaded: messagesLoaded } = useMessages(conversationId)
  const messages = topicId ? allMessages.filter((message) => message.topic?.id === topicId) : allMessages
  const [loadedTopic, setTopic] = useState<ChannelTopic | null>(null)
  const [topicHasMore, setTopicHasMore] = useState(false)
  const [topicBefore, setTopicBefore] = useState<number | null>(null)

  /*
   * Изменение v1.10: в группе над чужим сообщением стоит имя автора, а слева — его аватар.
   * Безопасные карточки приходят вместе с историей: список участников доступен только
   * администраторам. В канале посты идут от самого канала и подпись человека скрыта.
   */
  const groupCard =
    conversation?.kind === 'channel' && conversation.channel?.format === 'group' ? conversation.channel : null
  const groupChannelId = groupCard?.id
  // A route can switch topics before the next metadata response arrives.
  const topic = loadedTopic?.id === topicId && loadedTopic?.channel_id === groupChannelId ? loadedTopic : null
  useEffect(() => {
    if (!topicId || !groupChannelId) return
    let cancelled = false
    void api
      .GET('/channels/{channelId}/topics/{topicId}', {
        params: { path: { channelId: groupChannelId, topicId } },
      })
      .then(unwrap)
      .then((value) => {
        if (!cancelled) setTopic(value)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [topicId, groupChannelId, conversation?.version, groupCard?.version])
  const senders = useChannelMembers(messages)

  const [composerMode, setComposerMode] = useState<ComposerMode>({ kind: 'new' })
  const pendingForward = useAppStore((s) => s.pendingForward)
  const setPendingForward = useAppStore((s) => s.setPendingForward)
  const [menuFor, setMenuFor] = useState<{ message: LocalMessage; anchor: DOMRect } | null>(null)
  const [chatMenuAnchor, setChatMenuAnchor] = useState<DOMRect | null>(null)
  const [highlightSeq, setHighlightSeq] = useState<number | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [viewerMediaId, setViewerMediaId] = useState<string | null>(null)
  // C-22: ссылка, для которой показано предупреждение.
  const [pendingLink, setPendingLink] = useState<ParsedLink | null>(null)
  const [muteOpen, setMuteOpen] = useState(false)
  const [confirm, setConfirm] = useState<'clear' | 'hide' | null>(null)
  // C-08 доступен и по deep link `/chats/{id}/search`.
  const searchRoute = useRouterState({ select: (state) => state.location.pathname.endsWith('/search') })
  const [searchOpen, setSearchOpen] = useState(searchRoute)
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null)
  /** Сообщение, которое пересылают (лист выбора диалога). */
  const [forwardFor, setForwardFor] = useState<LocalMessage | null>(null)
  const [batchForwardOpen, setBatchForwardOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [batchBusy, setBatchBusy] = useState(false)
  const batchBusyRef = useRef(false)
  const lastSelectedSeqRef = useRef<number | null>(null)
  const selectedMessages = messages
    .filter((item) => selectedIds.includes(item.id) && !item.deleted)
    .sort((a, b) => a.seq - b.seq)
  const allDeleteCount = selectedMessages.filter((item) => canDeleteForAll(item, config, myUserId)).length
  useOverlayHistory(selectedIds.length > 0 && !batchForwardOpen, () => {
    setSelectedIds([])
    setBatchForwardOpen(false)
  })
  /** Закрепить и переслать можно только сообщение с серверным id — не из очереди отправки. */
  const menuConfirmed = menuFor ? !UNCONFIRMED_STATES.includes(menuFor.message.local_state) : false
  const menuLostForwardSource = Boolean(
    menuFor &&
      ((menuFor.message.forwarded && !menuFor.message.local_forward_of) ||
        menuFor.message.error_code === 'forward_source_invalid'),
  )
  // C-25: подсветка области чата при перетаскивании файла.
  const [dragActive, setDragActive] = useState(false)
  const [droppedFile, setDroppedFile] = useState<File | null>(null)
  const dragDepthRef = useRef(0)

  const historyHandleRef = useRef<MessageHistoryHandle>(null)
  const restoredRef = useRef(false)
  /*
   * Граница непрочитанного фиксируется при открытии диалога: `markRead` ниже
   * почти сразу обнулит счётчик в реплике, а плашка «Новые сообщения» должна
   * остаться на месте, пока диалог открыт.
   */
  const firstUnreadRef = useRef<{ conversationId: string; seq: number | null } | null>(null)

  /*
   * На mobile диалог — вложенный экран (ScreenFrame): входит «толчком» и уходит
   * кнопкой или жестом от края. «Назад» ведёт историей — туда, откуда диалог
   * открыли (список, профиль, уведомление); по deep link истории нет — в список.
   */
  const navigateBack = useCallback((): void => {
    if (topicId && conversationId) {
      void navigate({ to: '/chats/$conversationId', params: { conversationId }, replace: true })
      return
    }
    if (hasInAppHistory()) router.history.back()
    else void navigate({ to: '/chats', replace: true })
  }, [navigate, router, topicId, conversationId])
  const goBack = (): void => {
    if (selectedIds.length) {
      setSelectedIds([])
      return
    }
    if (frameRef.current) frameRef.current.pop(navigateBack)
    else navigateBack()
  }

  useEffect(() => {
    setSelectedIds([])
    setBatchForwardOpen(false)
    lastSelectedSeqRef.current = null
  }, [conversationId])

  useEffect(() => {
    setSelectedIds((current) => {
      if (!current.length) return current
      const live = current.filter((id) => messages.some((item) => item.id === id && !item.deleted))
      return live.length === current.length ? current : live
    })
  }, [messages])

  useEffect(() => {
    if (!selectedIds.length) return
    const onEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setSelectedIds([])
      setBatchForwardOpen(false)
    }
    document.addEventListener('keydown', onEscape, true)
    return () => document.removeEventListener('keydown', onEscape, true)
  }, [selectedIds.length])

  const toggleSelection = (message: LocalMessage, range = false): void => {
    if (message.deleted || message.sender_id === null || UNCONFIRMED_STATES.includes(message.local_state))
      return
    const candidates =
      range && lastSelectedSeqRef.current !== null
        ? messages.filter(
            (item) =>
              item.seq >= Math.min(lastSelectedSeqRef.current!, message.seq) &&
              item.seq <= Math.max(lastSelectedSeqRef.current!, message.seq) &&
              !item.deleted &&
              item.sender_id !== null &&
              !UNCONFIRMED_STATES.includes(item.local_state),
          )
        : [message]
    if (
      candidates.some((item) => !selectedIds.includes(item.id)) &&
      new Set([...selectedIds, ...candidates.map((item) => item.id)]).size > 100
    )
      toast.show('Можно выбрать не более 100 сообщений')
    setSelectedIds((current) => {
      const next = new Set(current)
      if (!range && next.has(message.id)) next.delete(message.id)
      else
        for (const item of candidates) {
          if (next.size >= 100) break
          next.add(item.id)
        }
      return [...next]
    })
    lastSelectedSeqRef.current = message.seq
  }

  const removeSelected = async (scope: 'me' | 'all'): Promise<void> => {
    if (batchBusyRef.current || !conversationId) return
    batchBusyRef.current = true
    setBatchBusy(true)
    const targets = selectedMessages.filter(
      (item) => scope === 'me' || canDeleteForAll(item, config, myUserId),
    )
    try {
      const { deleted, failed } = await deleteBatch(targets, async (item) => {
        try {
          await api.DELETE('/messages/{messageId}', {
            params: { path: { messageId: item.id }, query: { scope } as never },
          })
        } catch (error) {
          if (!(scope === 'me' && error instanceof ApiError && error.code === 'not_found')) throw error
        }
        await messagesRepo.finalizeDelete(item.id, scope)
      })
      setSelectedIds((current) => current.filter((id) => !deleted.includes(id)))
      if (failed.length || deleted.length < targets.length)
        toast.show(
          `Удалено ${deleted.length} из ${targets.length}; недоступные сообщения остались выбранными`,
          { tone: 'danger' },
        )
      else
        toast.show(
          `Удалено ${deleted.length}${scope === 'all' && deleted.length < selectedMessages.length ? '; остальные нельзя удалить у всех' : ''}`,
        )
    } finally {
      batchBusyRef.current = false
      setBatchBusy(false)
    }
  }

  const forwardSelected = async (targetConversationId: string): Promise<boolean> => {
    if (batchBusyRef.current) return false
    batchBusyRef.current = true
    setBatchBusy(true)
    const targets = selectedMessages
    try {
      await enqueueForwardBatch(targets, targetConversationId, myUserId)
      setSelectedIds([])
      setBatchForwardOpen(false)
      toast.show(`${targets.length} сообщ. поставлено в очередь на пересылку`)
      return true
    } catch {
      toast.show('Не удалось сохранить пакет пересылки; сообщения остались выбранными', { tone: 'danger' })
      return false
    } finally {
      batchBusyRef.current = false
      setBatchBusy(false)
    }
  }

  /*
   * §3.4 п. 5 / IOS §3.2: под экранной клавиатурой вся раскладка сжимается до
   * видимой области (app/model/viewport.ts) — композер остаётся над клавиатурой
   * без отдельного отступа. Якорь истории по `seq` при изменении высоты держит сама лента.
   */

  /* --- Подсказка подавления push (BS §13.3, CC §4.5) ------------------- */

  useEffect(() => {
    if (!conversationId) return

    // Зеркало обслуживает и кадр `active_chat`, и запись для service worker'а,
    // и снятие показанных уведомлений серии (§8.5).
    // A topic shows only part of the group. Suppressing the entire group would
    // discard notifications from other topics while this one is open.
    setActiveConversation(topicId ? null : conversationId)

    // §8.1 п. 2: вторая точка входа в N-03 — первое открытие чата.
    void offerPushPrompt()

    return () => setActiveConversation(null)
  }, [conversationId, topicId])

  /* --- Режим композера при смене диалога --------------------------------- */

  /*
   * Диалог-цель пересылки открывается с композером в режиме «переслать»;
   * любой другой переход снимает режим, привязанный к сообщению прежнего
   * диалога. Один эффект, а не два: порядок двух эффектов на монтировании
   * сбрасывал бы только что выставленный режим пересылки.
   */
  const modeForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!conversationId) return

    if (pendingForward?.targetConversationId === conversationId) {
      modeForRef.current = conversationId
      setComposerMode({ kind: 'forward', message: pendingForward.message })
      setPendingForward(null)
      return
    }

    if (modeForRef.current !== conversationId) {
      modeForRef.current = conversationId
      setComposerMode({ kind: 'new' })
    }
  }, [conversationId, pendingForward, setPendingForward])

  /* --- Индикатор набора собеседника ------------------------------------- */

  const typing = useTyping(conversationId)

  /* --- Первичная загрузка истории ----------------------------------------- */

  /*
   * Реплика хранит списки диалогов, но не сообщения: у диалога с историей
   * локальный список пуст, пока не придёт первая страница. Пока она не пришла,
   * «Начните разговор» не показывается — иначе карточка мелькала на кадр при
   * каждом открытии.
   */
  const [historyReady, setHistoryReady] = useState(false)
  useEffect(() => {
    if (!conversationId || !myUserId) return
    setHistoryReady(false)
    let cancelled = false
    if (topicId && groupChannelId) {
      void (async () => {
        try {
          const [metadata, page] = await Promise.all([
            api
              .GET('/channels/{channelId}/topics/{topicId}', {
                params: { path: { channelId: groupChannelId, topicId } },
              })
              .then(unwrap),
            api
              .GET('/conversations/{conversationId}/messages', {
                params: { path: { conversationId }, query: { topic_id: topicId, limit: 50 } },
              })
              .then(unwrap),
          ])
          if (cancelled) return
          setTopic(metadata)
          const currentConversation = await conversationsRepo.get(conversationId)
          if (cancelled) return
          await messagesRepo.putManyConfirmed(page.items, myUserId, currentConversation?.counterpart_receipts)
          if (!cancelled) {
            setTopicHasMore(page.has_more)
            setTopicBefore(page.items.length ? Math.min(...page.items.map((message) => message.seq)) : null)
          }
        } catch {
          if (!cancelled) toast.show('Не удалось открыть тему. Проверьте соединение.')
        } finally {
          if (!cancelled) setHistoryReady(true)
        }
      })()
    } else {
      void actions.ensureHistory(conversationId, myUserId).finally(() => {
        if (!cancelled) setHistoryReady(true)
      })
    }
    return () => {
      cancelled = true
    }
  }, [actions, conversationId, myUserId, topicId, groupChannelId, toast])

  /* --- §4.6: `↑` в пустом поле открывает правку последнего сообщения ----- */

  const editLastOwnMessage = useCallback((): boolean => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const candidate = messages[index]
      if (!candidate) continue
      if (candidate.sender_id !== myUserId) continue

      // Первое своё сообщение с конца решает исход: если его править нельзя,
      // «перепрыгивать» к более старому было бы неожиданно.
      // Локальный id queued/sending/failed серверу неизвестен: PATCH такого
      // сообщения всегда завершится 404. Клавиатурный путь совпадает с меню.
      if (UNCONFIRMED_STATES.includes(candidate.local_state)) return false
      return canEditMessage(candidate, config, myUserId)
        ? (setComposerMode({ kind: 'edit', message: candidate }), true)
        : false
    }

    return false
  }, [config, messages, myUserId])

  /* --- Прокрутка историей управляет сама лента (D-03, CC §2.6 п. 5) ------- */

  const loadOlder = useCallback(async (): Promise<boolean | void> => {
    if (!conversationId || loadingOlder) return

    setLoadingOlder(true)
    try {
      if (topicId && groupChannelId) {
        if (!topicHasMore) return false
        const page = unwrap(
          await api.GET('/conversations/{conversationId}/messages', {
            params: {
              path: { conversationId },
              query: { topic_id: topicId, limit: 50, ...(topicBefore ? { before: topicBefore } : {}) },
            },
          }),
        )
        await messagesRepo.putManyConfirmed(page.items, myUserId, conversation?.counterpart_receipts)
        setTopicHasMore(page.has_more)
        if (page.items.length) setTopicBefore(Math.min(...page.items.map((message) => message.seq)))
        return page.items.length > 0
      }
      return await actions.loadOlder(conversationId, myUserId)
    } finally {
      setLoadingOlder(false)
    }
  }, [
    actions,
    conversationId,
    loadingOlder,
    myUserId,
    topicId,
    groupChannelId,
    topicHasMore,
    topicBefore,
    conversation?.counterpart_receipts,
  ])

  /*
   * D-03 §3.5 п. 5: после перестройки раскладки история открывается там же,
   * где была, — по `seq` последнего видимого сообщения, а не «в самом низу».
   */
  useLayoutEffect(() => {
    if (!conversationId || restoredRef.current) return

    const anchor = takeHistoryAnchor(conversationId)
    if (anchor === null) return

    if (historyHandleRef.current?.restoreToSeq(anchor)) restoredRef.current = true
  }, [conversationId, messages.length])

  useEffect(() => {
    restoredRef.current = false
  }, [conversationId])

  /* --- Отметка прочтения --------------------------------------------------- */

  const [visibleAnchor, setVisibleAnchor] = useState<number | null>(null)
  const lastVisibleSeq = useCallback(() => historyHandleRef.current?.lastVisibleSeq() ?? null, [])
  useReadReceipts({
    conversation,
    historyReady,
    hidden: searchOpen || Boolean(topicId) || conversation?.request_role === 'incoming',
    messages,
    visibleAnchor,
    lastVisibleSeq,
    markRead: actions.markRead,
  })
  useEffect(() => {
    if (
      !topicId ||
      !groupChannelId ||
      !topic ||
      !historyReady ||
      searchOpen ||
      topic.unread_count === 0 ||
      document.visibilityState !== 'visible'
    )
      return
    const seq = lastVisibleSeq()
    if (!seq || seq <= topic.last_read_seq || seq > (conversation?.last_seq ?? 0)) return
    const timer = setTimeout(() => {
      void api
        .POST('/channels/{channelId}/topics/{topicId}/read', {
          params: { path: { channelId: groupChannelId, topicId } },
          body: { seq },
        })
        .then((response) => setTopic(unwrap(response)))
        .catch(() => undefined)
    }, 400)
    return () => clearTimeout(timer)
  }, [
    topicId,
    groupChannelId,
    topic,
    historyReady,
    searchOpen,
    visibleAnchor,
    messages.length,
    lastVisibleSeq,
    conversation?.last_seq,
  ])
  const handleAnchorChange = useCallback(
    (seq: number | null) => {
      if (conversationId) rememberHistoryAnchor(conversationId, seq)
      setVisibleAnchor(seq)
    },
    [conversationId],
  )

  /* --- Отправка -------------------------------------------------------------- */

  const handleSend = useCallback(
    async (
      text: string,
      media?: LocalMediaDraft | null,
      draftVersion?: number,
      entities?: Message['entities'],
      mediaIds?: string[],
    ) => {
      if (!conversationId) return

      if (composerMode.kind === 'edit') {
        await actions.edit(conversationId, composerMode.message, text, draftVersion, entities)
        setComposerMode({ kind: 'new' })
        return
      }

      if (composerMode.kind === 'forward') {
        const source = composerMode.message
        await actions.send({
          conversationId,
          senderId: myUserId,
          ...(topic ? { topic: { id: topic.id, title: topic.title } } : {}),
          text: '',
          forwardOf: source.id,
          draftKey: `${conversationId}::forward`,
          preserveDraft: Boolean(text.trim()),
          draftVersion: text.trim() ? undefined : draftVersion,
          // `system` сервером не создаётся (BS §7.5), в меню такого сообщения нет
          forwardType: source.type === 'system' || source.type === 'poll' ? 'text' : source.type,
        })
        // Комментарий к пересылке — отдельным сообщением следом, как в Telegram.
        if (text.trim())
          await actions.send({
            conversationId,
            senderId: myUserId,
            ...(topic ? { topic: { id: topic.id, title: topic.title } } : {}),
            text,
            entities,
            draftVersion,
            draftKey: `${conversationId}::forward`,
          })
        setComposerMode({ kind: 'new' })
        return
      }

      await actions.send({
        conversationId,
        senderId: myUserId,
        ...(topic ? { topic: { id: topic.id, title: topic.title } } : {}),
        text,
        entities,
        draftVersion,
        replyTo: composerMode.kind === 'reply' ? toReplyPreview(composerMode.message) : null,
        media: media ?? null,
        mediaIds,
      })

      setComposerMode({ kind: 'new' })
    },
    [actions, composerMode, conversationId, myUserId, topic],
  )

  /*
   * Переход к сообщению из поиска (C-08) или из цитаты ответа. Пока открыт
   * поиск, лента скрыта и прокручивать её бессмысленно — переход откладывается
   * до закрытия панели и выполняется, когда лента снова в раскладке.
   */
  const searchParams = useSearch({ strict: false }) as { seq?: number }
  const [scrollTarget, setScrollTarget] = useState<number | null>(searchParams.seq ?? null)
  useEffect(() => {
    if (searchParams.seq) setScrollTarget(searchParams.seq)
  }, [conversationId, searchParams.seq])

  const scrollToSeq = useCallback((seq: number) => {
    setScrollTarget(seq)
    setSearchOpen(false)
  }, [])

  useEffect(() => {
    if (scrollTarget === null || !conversationId) return
    const controller = new AbortController()
    const loadTarget = topicId
      ? api
          .GET('/conversations/{conversationId}/messages', {
            params: {
              path: { conversationId },
              query: { topic_id: topicId, around_seq: scrollTarget, limit: 50 },
            },
            signal: controller.signal,
          })
          .then(unwrap)
          .then(async (page) => {
            await messagesRepo.putManyConfirmed(page.items, myUserId, conversation?.counterpart_receipts)
            return page.items.some((message) => message.seq === scrollTarget)
          })
      : actions.loadThrough(conversationId, myUserId, scrollTarget, controller.signal)
    void loadTarget
      .then((found) => {
        if (!controller.signal.aborted && !found) {
          setScrollTarget(null)
          toast.show('Сообщение больше недоступно в истории.')
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setScrollTarget(null)
          toast.show('Не удалось открыть сообщение. Повторите поиск.')
        }
      })
    return () => controller.abort()
  }, [actions, conversationId, myUserId, scrollTarget, toast, topicId, conversation?.counterpart_receipts])

  useLayoutEffect(() => {
    if (searchOpen || scrollTarget === null || !messages.some((message) => message.seq === scrollTarget))
      return
    historyHandleRef.current?.scrollToSeq(scrollTarget)
    setHighlightSeq(scrollTarget)
    setScrollTarget(null)
  }, [messages, searchOpen, scrollTarget])

  useEffect(() => {
    if (highlightSeq === null) return
    const timer = setTimeout(() => setHighlightSeq(null), 1800)
    return () => clearTimeout(timer)
  }, [highlightSeq])

  /*
   * §4.6 п. 3, п. 5: `Ctrl/Cmd + F` — единственное браузерное сочетание,
   * которое разрешено перехватывать, и только при открытом диалоге.
   * Видимый эквивалент — кнопка поиска в шапке.
   */
  useEffect(() => {
    if (!conversationId) return

    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'f') return
      if (!hotkeysAvailable()) return

      event.preventDefault()
      setSearchOpen(true)
    }

    globalThis.addEventListener('keydown', onKeyDown)
    return () => globalThis.removeEventListener('keydown', onKeyDown)
  }, [conversationId])

  // Смена диалога закрывает поиск: результаты относятся к прежней переписке.
  useEffect(() => setSearchOpen(searchRoute), [conversationId, searchRoute])

  /* --- Состояния экрана ------------------------------------------------------- */

  if (!conversationId) return null

  /*
   * Загрузка и «диалог недоступен» — в той же рамке, что и сам диалог: рамка монтируется
   * сразу и въезжает со снимком списка под ней, а содержимое дорисовывается внутри.
   * Без рамки экран загрузки вставал на место без движения, а push стартовал только
   * после ответа реплики — диалог вспыхивал, пропадал и въезжал заново.
   */
  if (conversation === undefined || conversation === null) {
    return (
      <ScreenFrame
        ref={frameRef}
        nested={!embedded}
        onBack={embedded ? undefined : navigateBack}
        className={styles['chat']}
      >
        <header className={styles['header']}>
          <button
            type="button"
            className={cx(styles['headerBack'], embedded && styles['headerBackHidden'])}
            aria-label="Назад"
            onClick={goBack}
          >
            <Icon name="back" size={22} />
          </button>
        </header>
        {conversation === undefined ? (
          <div className={styles['loading']}>
            <Spinner size={28} label="Загрузка диалога" />
          </div>
        ) : (
          // SCREEN_CATALOG §15: `not_found` → SYS-15 и возврат в список.
          <EmptyState
            icon="alert"
            title="Диалог недоступен"
            text="Возможно, он был закрыт или удалён."
            actions={
              <Button variant="primary" onClick={() => void navigate({ to: '/chats' })}>
                К списку чатов
              </Button>
            }
          />
        )}
      </ScreenFrame>
    )
  }

  if (firstUnreadRef.current?.conversationId !== conversation.id) {
    firstUnreadRef.current = {
      conversationId: conversation.id,
      seq: conversation.unread_count > 0 ? conversation.my.last_read_seq + 1 : null,
    }
  }
  const firstUnreadSeq = topicId ? null : firstUnreadRef.current.seq

  const counterpart = conversation.counterpart
  // Изменение v1.7: у ленты канала контрагента нет — имя, аватар и доступность берутся из карточки.
  const face = faceOf(conversation)
  const channel = face.channel
  const unavailable = face.availability !== 'available'
  const closed = conversation.state === 'closed'
  const pendingRecipient = conversation.request_role === 'incoming'
  const pendingSender = conversation.request_role === 'sent'
  const acceptRequest = async () => {
    try {
      const accepted = unwrap(
        await api.POST('/conversations/{conversationId}/request/accept', {
          params: { path: { conversationId } },
        }),
      )
      await conversationsRepo.put(accepted)
      await queryClient.invalidateQueries({ queryKey: MESSAGE_REQUESTS_KEY })
      toast.show('Запрос принят')
    } catch {
      toast.show('Не удалось принять запрос', { tone: 'danger' })
    }
  }
  const blockRequest = async () => {
    const userId = conversation.counterpart?.id
    if (!userId) return
    try {
      unwrap(
        await api.POST('/users/{userId}/block', {
          params: { path: { userId } },
          body: { reason_class: 'spam' },
          ...idempotency(uuid()),
        }),
      )
      await queryClient.invalidateQueries({ queryKey: MESSAGE_REQUESTS_KEY })
      toast.show('Пользователь заблокирован')
      void navigate({ to: '/chats/requests' })
    } catch {
      toast.show('Не удалось заблокировать пользователя', { tone: 'danger' })
    }
  }
  // В канале пишут только владелец и администраторы; остальным композер заменяет полоса подписки.
  const readerOnly =
    channel !== null &&
    !(channel.capabilities
      ? channel.capabilities.send_text || channel.capabilities.send_media
      : channel.can_post)
  const canPin = !channel || Boolean(channel.capabilities?.pin)
  const counterpartName = channel
    ? channel.title
    : unavailable
      ? face.availability === 'deleted'
        ? 'Удалённый аккаунт'
        : 'Профиль недоступен'
      : face.name

  /*
   * C-25 «Зона drag-and-drop». Счётчик глубины нужен потому, что
   * `dragleave` приходит и при переходе между вложенными элементами —
   * без него подсветка мигала бы на каждом пузыре сообщения.
   */
  const dragProps =
    closed ||
    unavailable ||
    readOnly ||
    readerOnly ||
    pendingRecipient ||
    channel?.capabilities?.send_media === false
      ? {}
      : {
          onDragEnter: (event: React.DragEvent) => {
            if (!event.dataTransfer.types.includes('Files')) return
            dragDepthRef.current += 1
            setDragActive(true)
          },
          onDragOver: (event: React.DragEvent) => {
            if (!event.dataTransfer.types.includes('Files')) return
            // Без preventDefault браузер откроет файл вместо передачи его нам.
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
          },
          onDragLeave: () => {
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
            if (dragDepthRef.current === 0) setDragActive(false)
          },
          onDrop: (event: React.DragEvent) => {
            event.preventDefault()
            dragDepthRef.current = 0
            setDragActive(false)
            setDroppedFile(event.dataTransfer.files[0] ?? null)
          },
        }

  return (
    <ScreenFrame
      ref={frameRef}
      nested={!embedded}
      onBack={embedded ? undefined : selectedIds.length ? () => setSelectedIds([]) : navigateBack}
      className={cx(styles['chat'], dragActive && styles['chatDropActive'])}
      {...dragProps}
    >
      {/* C-25: подсказка появляется только во время перетаскивания */}
      {dragActive ? (
        <div className={styles['dropHint']} aria-hidden="true">
          <Icon name="image" size={28} />
          <span>Отпустите, чтобы отправить файл</span>
        </div>
      ) : null}

      {/* --- Шапка --- */}
      <header className={styles['header']}>
        <button
          type="button"
          className={cx(styles['headerBack'], embedded && styles['headerBackHidden'])}
          aria-label="Назад"
          onClick={goBack}
        >
          <Icon name="back" size={22} />
        </button>

        {selectedIds.length ? (
          <strong className={styles['selectionCount']}>{selectedIds.length} выбрано</strong>
        ) : (
          <button
            type="button"
            className={styles['headerMain']}
            onClick={() => {
              if (topicId) {
                void navigate({ to: '/chats/$conversationId', params: { conversationId } })
                return
              }
              if (conversation.kind === 'saved')
                void navigate({ to: '/chats/$conversationId/shared', params: { conversationId } })
              else if (channel)
                void navigate({ to: '/channels/$channelId', params: { channelId: channel.id } })
              else if (counterpart && !unavailable) {
                void navigate({ to: '/users/$userId', params: { userId: counterpart.id } })
              }
            }}
          >
            <Avatar
              name={face.name}
              photo={face.photo}
              availability={face.availability}
              size="md"
              online={counterpart?.presence?.online ?? false}
            />

            <span className={styles['headerText']}>
              <span className={styles['headerName']}>{topic ? `# ${topic.title}` : counterpartName}</span>
              <span
                className={cx(
                  styles['headerStatus'],
                  typing && styles['headerStatusTyping'],
                  !typing && counterpart?.presence?.online && styles['headerStatusOnline'],
                )}
              >
                {topic
                  ? `${channel?.title ?? 'Группа'} · ${topic.state === 'active' ? 'Обсуждение' : topic.state === 'closed' ? 'Закрыта' : 'В архиве'}`
                  : channel
                    ? subscribersLabel(channel.subscribers_count, channel.format)
                    : typing
                      ? 'печатает…'
                      : (formatPresence(counterpart?.presence ?? null) ??
                        (face.username && !unavailable ? `@${face.username}` : ''))}
              </span>
            </span>
          </button>
        )}

        {!selectedIds.length ? (
          <div className={styles['headerActions']}>
            {/* C-08: поиск по диалогу — видимый эквивалент Ctrl/Cmd + F (§4.6 п. 5) */}
            <Button
              icon="search"
              variant="ghost"
              aria-label="Поиск по диалогу"
              onClick={() => setSearchOpen(true)}
            />
            <Button
              icon="more"
              variant="ghost"
              aria-label="Действия с диалогом"
              onClick={(event) => setChatMenuAnchor(event.currentTarget.getBoundingClientRect())}
            />
          </div>
        ) : (
          <button
            type="button"
            className={styles['selectionDone']}
            onClick={() => setSelectedIds([])}
            aria-label="Отменить выбор"
          >
            Готово
          </button>
        )}
      </header>

      {pendingRecipient ? (
        <div className={styles['closedNotice']} role="region" aria-label="Запрос на переписку">
          <span>
            Незнакомый человек хочет начать переписку. Вложения скрыты; прочтение и активность не показываются
            до принятия.
          </span>
          <Button onClick={() => void acceptRequest()}>Принять</Button>
          <Button variant="ghost" onClick={() => void blockRequest()}>
            Заблокировать
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              setReportTarget({
                type: 'user',
                id: conversation.counterpart!.id,
                title: 'Пожаловаться на запрос',
                subject: face.name,
              })
            }
          >
            Пожаловаться
          </Button>
        </div>
      ) : pendingSender ? (
        <div className={styles['closedNotice']}>Запрос отправлен. Собеседник ещё не принял переписку.</div>
      ) : null}

      {/* --- C-08: поиск вместо истории, пока он открыт --- */}
      {/* Закреплённое сообщение — плашка под шапкой, как в Telegram; нажатие ведёт к сообщению */}
      {pinned && !pinned.deleted && !searchOpen ? (
        <div className={styles['pinnedBar']}>
          <button
            type="button"
            className={styles['pinnedMain']}
            onClick={() => historyHandleRef.current?.scrollToSeq(pinned.seq)}
          >
            <span className={styles['pinnedTitle']}>Закреплённое сообщение</span>
            <span className={styles['pinnedText']}>{pinned.text_preview ?? mediaLabel(pinned.type)}</span>
          </button>
          {canPin ? (
            <button
              type="button"
              className={styles['pinnedClose']}
              aria-label="Открепить"
              onClick={() => void actions.pin(conversationId, null)}
            >
              <Icon name="close" size={18} />
            </button>
          ) : null}
        </div>
      ) : null}

      {searchOpen ? (
        <ChatSearch
          conversationId={conversationId}
          myUserId={myUserId}
          topicId={topicId}
          onPick={scrollToSeq}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}

      {/* --- История (C-07) --- */}
      <MessageHistory
        handleRef={historyHandleRef}
        messages={messages}
        myUserId={myUserId}
        counterpartName={face.name}
        senders={groupCard ? senders : undefined}
        highlightSeq={highlightSeq}
        onOpenComments={
          channel?.comments_enabled
            ? (post) =>
                void navigate({
                  to: '/chats/$conversationId/posts/$postId',
                  params: { conversationId, postId: post.id },
                })
            : undefined
        }
        loadingOlder={loadingOlder}
        hidden={searchOpen}
        onLoadOlder={loadOlder}
        onOpenMedia={(mediaId) => setViewerMediaId(mediaId)}
        onContextMenu={(target, anchor) => setMenuFor({ message: target, anchor })}
        selectedIds={selectedIds}
        selectionActive={selectedIds.length > 0}
        onToggleSelection={toggleSelection}
        onRetry={(target) => {
          if (target.client_message_id) void actions.retry(conversationId, target.client_message_id)
        }}
        onDiscard={(target) => {
          if (target.client_message_id) void actions.discard(conversationId, target.client_message_id)
        }}
        onOpenLink={setPendingLink}
        onReact={(target, reaction) =>
          void actions.react(conversationId, { id: target.id, seq: target.seq }, reaction, myUserId)
        }
        onReply={(target) => setComposerMode({ kind: 'reply', message: target })}
        quickReaction={config?.reactions[0] ?? null}
        onAnchorChange={handleAnchorChange}
        firstUnreadSeq={firstUnreadSeq}
        empty={
          // Только когда и реплика ответила, и первая страница истории загружена
          messagesLoaded && historyReady ? (
            <div className={styles['emptyCard']}>
              <Icon
                name={
                  conversation.kind === 'saved' ? 'pushpin' : channel ? spaceIcon(channel.format) : 'sparkle'
                }
                size={28}
              />
              <div className={styles['emptyTitle']}>
                {conversation.kind === 'saved'
                  ? 'Сохраните важное'
                  : channel && channel.format === 'channel'
                    ? 'Пока нет публикаций'
                    : 'Начните разговор'}
              </div>
              <p className={styles['emptyText']}>
                {conversation.kind === 'saved'
                  ? 'Заметки, сообщения и файлы будут доступны только вам'
                  : channel
                    ? channel.format === 'group'
                      ? 'Напишите первым — сообщение увидят все участники'
                      : channel.can_post
                        ? 'Опубликуйте первое сообщение — его увидят подписчики'
                        : 'Автор ещё ничего не опубликовал'
                    : 'Вы оба за движ — напишите первым'}
              </p>
            </div>
          ) : null
        }
        typing={
          typing ? (
            <div className={styles['typingRow']}>
              <span className={styles['typingDots']} aria-label="Собеседник печатает">
                <span className={styles['typingDot']} />
                <span className={styles['typingDot']} />
                <span className={styles['typingDot']} />
              </span>
            </div>
          ) : null
        }
      />

      {/* --- Композер, полоса канала или сообщение о закрытии (C-21, SYS-16, C-30) --- */}
      {selectedIds.length ? (
        <div
          className={styles['selectionToolbar']}
          role="toolbar"
          aria-label="Действия с выбранными сообщениями"
        >
          <button type="button" disabled={batchBusy} onClick={() => void removeSelected('me')}>
            Удалить у меня
          </button>
          <button
            type="button"
            disabled={batchBusy || !allDeleteCount}
            onClick={() => void removeSelected('all')}
          >
            Удалить у всех ({allDeleteCount})
          </button>
          <button type="button" disabled={batchBusy} onClick={() => setBatchForwardOpen(true)}>
            Переслать ({selectedIds.length})
          </button>
        </div>
      ) : readerOnly && !closed ? (
        <Suspense fallback={null}>
          <ChannelFooter channel={channel} />
        </Suspense>
      ) : closed ||
        unavailable ||
        readOnly ||
        pendingRecipient ||
        (topicId && (!topic || topic.state !== 'active')) ? (
        <div className={styles['closedNotice']}>
          <Icon name="lock" size={16} />
          <span>
            {/* SYS-16: при `read_only` объясняем причину, а не гасим кнопку молча */}
            {topicId && topic?.state !== 'active'
              ? 'Тема закрыта. История доступна для чтения.'
              : pendingRecipient
                ? 'Примите запрос, чтобы ответить.'
                : readOnly
                  ? 'Отправка сообщений ограничена по решению модерации. История доступна для чтения.'
                  : unavailable
                    ? 'Профиль недоступен. Отправка сообщений отключена.'
                    : 'Диалог закрыт. История доступна только для чтения.'}
          </span>
        </div>
      ) : (
        <Composer
          key={conversationId}
          channelId={groupCard?.id}
          allowText={channel?.capabilities?.send_text ?? true}
          allowMedia={channel?.capabilities?.send_media ?? true}
          onCreatePoll={() => {
            if (topicId)
              void navigate({
                to: '/chats/$conversationId/topics/$topicId/poll/new',
                params: { conversationId, topicId },
              })
            else void navigate({ to: '/chats/$conversationId/poll/new', params: { conversationId } })
          }}
          onEditLast={editLastOwnMessage}
          droppedFile={droppedFile}
          onDroppedFileTaken={() => setDroppedFile(null)}
          conversationId={conversationId}
          mode={composerMode}
          onModeChange={setComposerMode}
          onSend={handleSend}
          counterpartName={face.name}
          myUserId={myUserId}
        />
      )}

      {/* --- SF-01 / SF-02: жалоба на сообщение или вложение --- */}
      <ReportDialog
        target={reportTarget}
        categories={config?.report_categories ?? []}
        onClose={() => setReportTarget(null)}
        onSubmitted={(reportId) => {
          const wasRequestReport = pendingRecipient && reportTarget?.type === 'user'
          setReportTarget(null)
          if (reportId) void uiStateRepo.set(UiKey.lastReportId, reportId)
          toast.show('Жалоба отправлена')
          if (wasRequestReport) void blockRequest()
        }}
      />

      {/* --- C-22: предупреждение перед переходом по внешней ссылке --- */}
      <ExternalLinkDialog link={pendingLink} onClose={() => setPendingLink(null)} />

      {/* --- C-09: полноэкранный просмотрщик --- */}
      {viewerMediaId ? (
        <Suspense fallback={null}>
          <MediaViewer
            messages={messages}
            mediaId={viewerMediaId}
            myUserId={myUserId}
            onClose={() => setViewerMediaId(null)}
            onReport={(message) => {
              setViewerMediaId(null)
              if (message.media) {
                setReportTarget({
                  type: 'media',
                  id: message.media.id,
                  title: 'Пожаловаться на вложение',
                })
              }
            }}
            onDeleteForAll={(message) => void actions.remove(conversationId, message.id, 'all')}
          />
        </Suspense>
      ) : null}

      {/* --- C-14: действия с сообщением --- */}
      <Sheet
        open={menuFor !== null}
        onClose={() => setMenuFor(null)}
        anchorRect={menuFor?.anchor ?? null}
        title="Сообщение"
        description={menuFor?.message.text ? excerpt(menuFor.message.text) : undefined}
        flush
      >
        {/* Быстрые реакции — набор из `GET /config → reactions` (BS §7.6), своя подсвечена */}
        {menuFor && menuConfirmed && !menuFor.message.deleted && config?.reactions.length ? (
          <div className={styles['reactionBar']} role="group" aria-label="Реакция">
            {config.reactions.map((emoji) => {
              const mine = menuFor.message.reaction_summary
                ? menuFor.message.reaction_summary.some(
                    (item) => item.reacted_by_me && item.reaction === emoji,
                  )
                : menuFor.message.reactions.some((r) => r.user_id === myUserId && r.reaction === emoji)
              return (
                <button
                  key={emoji}
                  type="button"
                  className={cx(styles['reactionChoice'], mine && styles['reactionChoiceActive'])}
                  aria-label={`Реакция ${emoji}`}
                  aria-pressed={mine}
                  onClick={() => {
                    const target = menuFor.message
                    setMenuFor(null)
                    void actions.react(
                      conversationId,
                      { id: target.id, seq: target.seq },
                      mine ? null : emoji,
                      myUserId,
                    )
                  }}
                >
                  {emoji}
                </button>
              )
            })}
          </div>
        ) : null}

        <ActionList>
          {/* C-19: неотправленное сообщение — сначала повтор и удаление черновика */}
          {menuFor?.message.local_state === 'failed' ? (
            <>
              {!menuLostForwardSource ? (
                <ActionItem
                  icon="refresh"
                  label="Повторить отправку"
                  onClick={() => {
                    const target = menuFor?.message
                    setMenuFor(null)
                    if (target?.client_message_id)
                      void actions.retry(conversationId, target.client_message_id)
                  }}
                />
              ) : null}
              <ActionItem
                icon="trash"
                label="Удалить неотправленное"
                danger
                onClick={() => {
                  const target = menuFor?.message
                  setMenuFor(null)
                  if (target?.client_message_id)
                    void actions.discard(conversationId, target.client_message_id)
                }}
              />
            </>
          ) : null}

          {menuFor && menuConfirmed ? (
            <ActionItem
              icon="reply"
              label="Ответить"
              onClick={() => {
                if (menuFor) setComposerMode({ kind: 'reply', message: menuFor.message })
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor?.message.text ? (
            <ActionItem
              icon="copy"
              label="Копировать текст"
              onClick={() => {
                const text = menuFor?.message.text ?? ''
                setMenuFor(null)
                void navigator.clipboard
                  .writeText(text)
                  .then(() => toast.show('Скопировано'))
                  .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
              }}
            />
          ) : null}

          {/* У неотправленного сообщения ещё нет серверного id, пересылать нечего */}
          {menuFor && !menuFor.message.deleted && menuFor.message.type !== 'poll' && menuConfirmed ? (
            <ActionItem
              icon="forward"
              label="Переслать"
              onClick={() => {
                const target = menuFor?.message ?? null
                setMenuFor(null)
                setForwardFor(target)
              }}
            />
          ) : null}

          {menuFor && !menuFor.message.deleted && menuConfirmed ? (
            <ActionItem
              icon="check"
              label="Выбрать"
              onClick={() => {
                toggleSelection(menuFor.message)
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor &&
          menuConfirmed &&
          canPin &&
          (conversation.state === 'active' || pinned?.id === menuFor.message.id) ? (
            <ActionItem
              icon="pushpin"
              label={pinned?.id === menuFor.message.id ? 'Открепить' : 'Закрепить'}
              onClick={() => {
                const target = menuFor?.message
                setMenuFor(null)
                if (target) void actions.pin(conversationId, pinned?.id === target.id ? null : target.id)
              }}
            />
          ) : null}

          {/* C-17: окно правки задано `limits.edit_window_sec` (BS §7.5) */}
          {menuFor && menuConfirmed && canEditMessage(menuFor.message, config, myUserId) ? (
            <ActionItem
              icon="edit"
              label="Изменить"
              onClick={() => {
                if (menuFor) setComposerMode({ kind: 'edit', message: menuFor.message })
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor && menuConfirmed ? (
            <ActionItem
              icon="trash"
              label="Удалить у меня"
              onClick={() => {
                if (menuFor) void actions.remove(conversationId, menuFor.message.id, 'me')
                setMenuFor(null)
              }}
            />
          ) : null}

          {/* Окно «удалить у всех» — `limits.delete_all_window_sec` (BS §7.5) */}
          {menuFor && menuConfirmed && canDeleteForAll(menuFor.message, config, myUserId) ? (
            <ActionItem
              icon="trash"
              label="Удалить у всех"
              danger
              onClick={() => {
                if (menuFor) void actions.remove(conversationId, menuFor.message.id, 'all')
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor && menuConfirmed && menuFor.message.sender_id !== myUserId ? (
            <ActionItem
              icon="flag"
              label="Пожаловаться на сообщение"
              danger
              onClick={() => {
                const target = menuFor?.message
                setMenuFor(null)
                if (target) setReportTarget(messageReportTarget(target, messages, counterpartName))
              }}
            />
          ) : null}
        </ActionList>
      </Sheet>

      <ForwardSheet message={forwardFor} excludeId={conversationId} onClose={() => setForwardFor(null)} />
      <ForwardSheet
        message={null}
        messages={batchForwardOpen ? selectedMessages : []}
        excludeId={conversationId}
        onClose={() => setBatchForwardOpen(false)}
        onBatchForward={forwardSelected}
      />

      {/* --- C-05: действия с диалогом --- */}
      <Sheet
        open={chatMenuAnchor !== null}
        onClose={() => setChatMenuAnchor(null)}
        anchorRect={chatMenuAnchor}
        title={topic?.title ?? counterpartName ?? 'Диалог'}
        flush
      >
        <ActionList>
          {groupCard &&
          !groupCard.topics_enabled &&
          !topicId &&
          (groupCard.my_role === 'owner' || groupCard.my_role === 'admin') ? (
            <ActionItem
              icon="comment"
              label="Включить темы"
              onClick={() => {
                setChatMenuAnchor(null)
                void (async () => {
                  try {
                    const channel = unwrap(
                      await api.POST('/channels/{channelId}/topics/enable', {
                        params: { path: { channelId: groupCard.id } },
                      }),
                    )
                    await conversationsRepo.patchOptimistic(conversationId, { channel })
                  } catch {
                    toast.show('Не удалось включить темы. Попробуйте ещё раз.')
                  }
                })()
              }}
            />
          ) : null}
          {topic && groupCard ? (
            <>
              <ActionItem
                icon="back"
                label="Все темы"
                onClick={() => {
                  setChatMenuAnchor(null)
                  void navigate({ to: '/chats/$conversationId', params: { conversationId } })
                }}
              />
              {(['all', 'mentions', 'none'] as const).map((mode) => (
                <ActionItem
                  key={mode}
                  icon={mode === 'none' ? 'bell-off' : 'bell'}
                  label={`${topic.notification_mode === mode ? '✓ ' : ''}${mode === 'all' ? 'Все уведомления' : mode === 'mentions' ? 'Только упоминания' : 'Без уведомлений'}`}
                  onClick={() => {
                    setChatMenuAnchor(null)
                    void api
                      .PATCH('/channels/{channelId}/topics/{topicId}/notifications', {
                        params: { path: { channelId: groupCard.id, topicId: topic.id } },
                        body: { notification_mode: mode },
                      })
                      .then((response) => setTopic(unwrap(response)))
                      .catch(() => toast.show('Не удалось изменить уведомления темы.'))
                  }}
                />
              ))}
              {(groupCard.my_role === 'owner' || groupCard.my_role === 'admin') && !topic.general ? (
                <>
                  <ActionItem
                    icon="lock"
                    label={topic.state === 'closed' ? 'Открыть тему' : 'Закрыть тему'}
                    onClick={() => {
                      setChatMenuAnchor(null)
                      void api
                        .PATCH('/channels/{channelId}/topics/{topicId}', {
                          params: { path: { channelId: groupCard.id, topicId: topic.id } },
                          body: { state: topic.state === 'closed' ? 'active' : 'closed' },
                        })
                        .then((response) => setTopic(unwrap(response)))
                        .catch(() => toast.show('Не удалось изменить состояние темы.'))
                    }}
                  />
                  <ActionItem
                    icon="archive"
                    label={topic.state === 'archived' ? 'Вернуть тему' : 'Архивировать тему'}
                    onClick={() => {
                      setChatMenuAnchor(null)
                      void api
                        .PATCH('/channels/{channelId}/topics/{topicId}', {
                          params: { path: { channelId: groupCard.id, topicId: topic.id } },
                          body: { state: topic.state === 'archived' ? 'active' : 'archived' },
                        })
                        .then((response) => setTopic(unwrap(response)))
                        .catch(() => toast.show('Не удалось изменить состояние темы.'))
                    }}
                  />
                </>
              ) : null}
            </>
          ) : null}
          <ActionItem
            icon="paperclip"
            label="Медиа, ссылки и файлы"
            onClick={() => {
              setChatMenuAnchor(null)
              void navigate({ to: '/chats/$conversationId/shared', params: { conversationId } })
            }}
          />
          {channel || counterpart ? (
            <ActionItem
              icon={channel ? spaceIcon(channel.format) : 'user'}
              label={channel ? (channel.format === 'group' ? 'О группе' : 'О канале') : 'Открыть профиль'}
              disabled={unavailable}
              onClick={() => {
                setChatMenuAnchor(null)
                if (channel) void navigate({ to: '/channels/$channelId', params: { channelId: channel.id } })
                else if (counterpart)
                  void navigate({ to: '/users/$userId', params: { userId: counterpart.id } })
              }}
            />
          ) : null}
          <ActionItem
            icon="bell-off"
            label={conversation.my.muted_until ? 'Включить уведомления' : 'Отключить уведомления'}
            onClick={() => {
              setChatMenuAnchor(null)
              // C-06: длительность выбирает пользователь, а не код.
              if (conversation.my.muted_until) void actions.setMuted(conversationId, null)
              else setMuteOpen(true)
            }}
          />
          <ActionItem
            icon="archive"
            label={conversation.my.archived_at ? 'Убрать из архива' : 'В архив'}
            onClick={() => {
              void actions.setArchived(conversationId, conversation.my.archived_at === null)
              setChatMenuAnchor(null)
              if (!conversation.my.archived_at) void navigate({ to: '/chats' })
            }}
          />
          <ActionItem
            icon="trash"
            label="Очистить историю"
            danger
            onClick={() => {
              // ПС §04: разрушающее действие подтверждается (C-24).
              setChatMenuAnchor(null)
              setConfirm('clear')
            }}
          />
          <ActionItem
            icon="trash"
            label="Удалить чат у меня"
            danger
            onClick={() => {
              setChatMenuAnchor(null)
              setConfirm('hide')
            }}
          />
        </ActionList>
      </Sheet>

      {/* --- C-06: длительность mute --- */}
      <Sheet
        open={muteOpen}
        onClose={() => setMuteOpen(false)}
        title="Отключить уведомления"
        description="Счётчик непрочитанного останется — mute влияет только на push."
        flush
      >
        <ActionList>
          {MUTE_OPTIONS.map((option) => (
            <ActionItem
              key={option.label}
              icon="bell-off"
              label={option.label}
              onClick={() => {
                setMuteOpen(false)
                void actions.setMuted(conversationId, new Date(Date.now() + option.ms).toISOString())
              }}
            />
          ))}
        </ActionList>
      </Sheet>

      {/* --- C-24: подтверждение очистки и удаления --- */}
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'hide' ? 'Удалить чат у себя?' : 'Очистить историю?'}
        description={
          confirm === 'hide'
            ? 'Диалог исчезнет только у вас и вернётся с новым сообщением.'
            : 'Сообщения исчезнут только у вас. У собеседника переписка останется.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const action = confirm
                setConfirm(null)
                if (action === 'clear') {
                  void actions
                    .clearHistory(conversationId)
                    .catch(() => toast.show('Не удалось очистить историю', { tone: 'danger' }))
                } else if (action === 'hide') {
                  void actions
                    .hideConversation(conversationId)
                    .then(() => navigate({ to: '/chats' }))
                    .catch(() => toast.show('Не удалось удалить чат', { tone: 'danger' }))
                }
              }}
            >
              {confirm === 'hide' ? 'Удалить' : 'Очистить'}
            </Button>
          </>
        }
      />
    </ScreenFrame>
  )
}

/** Короткая выдержка текста для описания листа действий. */
function excerpt(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > 90 ? `${flat.slice(0, 90)}…` : flat
}

function toReplyPreview(message: LocalMessage): Message['reply_to'] {
  return {
    id: message.id,
    seq: message.seq,
    sender: message.sender ?? null,
    sender_id: message.sender_id ?? '',
    type: 'text',
    text_preview: message.text?.slice(0, 120) ?? null,
    deleted: message.deleted,
  }
}
